import {
  model,
  Model,
  prop,
  idProp,
  registerRootStore,
  modelAction,
  Frozen,
  frozen,
} from "mobx-keystone";
import { computed } from "mobx";
import { createContext } from "react";
import { AddressInfo, LightningAddressInfo } from "../helpers/types";
import { identifyAddress } from "../helpers/addressIdentifier";
import { checkLightningAddress } from "../helpers/lightningAddressChecker";
import {
  AddressLookupResult,
  LOOKUP_TYPES,
  lookupAddress,
} from "../helpers/addressLookup";
import {
  quotaMessage,
  requestToolQuota,
} from "../../lightningAddress/helpers/toolQuota";
import { trackGoal } from "../../helpers/trackGoal";

@model("exrails/CheckBitcoinAddressStore")
export class CheckBitcoinAddressStore extends Model({
  id: idProp,
  addressInput: prop<string>(""),
  /** Result of local identification */
  addressInfo: prop<Frozen<AddressInfo> | null>(null),
  /** Result of Lightning Address server check */
  lightningInfo: prop<Frozen<LightningAddressInfo> | null>(null),
  /** Whether we're currently checking a Lightning Address */
  isCheckingLightning: prop<boolean>(false),
  /** Whether the user has pressed "Check" at least once */
  hasChecked: prop<boolean>(false),
  /** Balance + latest transactions from /api/address_lookup (mainnet addresses only) */
  lookup: prop<Frozen<AddressLookupResult> | null>(null),
  isLookingUp: prop<boolean>(false),
  lookupError: prop<string | null>(null),
  /** the lookup limit was hit without an account: offer log in / sign up */
  lookupNeedsLogin: prop<boolean>(false),
}) {
  @modelAction
  hydrateFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const addrParam = params.get("address");
    if (addrParam) {
      this.addressInput = addrParam;
      this.checkAddress();
    }
  };

  @modelAction
  setAddressInput = (value: string) => {
    this.addressInput = value;
    // Reset results when input changes
    this.addressInfo = null;
    this.lightningInfo = null;
    this.hasChecked = false;
    this.resetLookup();
  };

  @modelAction
  resetLookup = () => {
    this.lookup = null;
    this.isLookingUp = false;
    this.lookupError = null;
    this.lookupNeedsLogin = false;
  };

  @modelAction
  setLookupStarted = () => {
    this.lookup = null;
    this.lookupError = null;
    this.lookupNeedsLogin = false;
    this.isLookingUp = true;
  };

  @modelAction
  setLookupResult = (
    result: AddressLookupResult | null,
    error: string | null,
    needsLogin: boolean,
  ) => {
    this.lookup = result ? frozen(result) : null;
    this.lookupError = error;
    this.lookupNeedsLogin = needsLogin;
    this.isLookingUp = false;
  };

  /** Balance + transactions, for mainnet Bitcoin addresses that passed the checksum */
  runLookup = async (address: string) => {
    this.setLookupStarted();
    const response = await lookupAddress(address);
    trackGoal("Address looked up", {
      result: response.result ? "ok" : response.status === "limited" ? "limited" : "error",
    });
    // the visitor may have typed something else while we waited
    if (this.addressInput.trim() !== address) return;
    this.setLookupResult(response.result, response.error, response.login);
  };

  @modelAction
  setAddressInfo = (info: AddressInfo) => {
    this.addressInfo = frozen(info);
    this.hasChecked = true;
  };

  @modelAction
  setLightningInfo = (info: LightningAddressInfo | null) => {
    this.lightningInfo = info ? frozen(info) : null;
  };

  @modelAction
  setIsCheckingLightning = (val: boolean) => {
    this.isCheckingLightning = val;
  };

  /** Main check action — runs local identification + Lightning Address check if applicable */
  checkAddress = async () => {
    const trimmed = this.addressInput.trim();
    if (!trimmed) return;

    const info = identifyAddress(trimmed);
    this.setAddressInfo(info);
    this.syncUrl();

    if (LOOKUP_TYPES.has(info.type)) {
      void this.runLookup(trimmed);
    } else {
      this.resetLookup();
    }

    // If it's a Lightning Address, also do the server check
    if (info.type === "lightning_address") {
      this.setIsCheckingLightning(true);
      try {
        // same per-IP lookup quota as /lightning-address (app/modules/ToolQuota.rb)
        const quota = await requestToolQuota("resolve", trimmed);
        if (quota !== "ok") {
          this.setLightningInfo({
            isValid: false,
            domain: trimmed.split("@")[1] || "",
            user: trimmed.split("@")[0] || "",
            error: quotaMessage("resolve", quota),
          });
          this.setIsCheckingLightning(false);
          return;
        }
        const lnInfo = await checkLightningAddress(trimmed);
        this.setLightningInfo(lnInfo);
      } catch {
        this.setLightningInfo({
          isValid: false,
          domain: trimmed.split("@")[1] || "",
          user: trimmed.split("@")[0] || "",
          error: "Unexpected error checking Lightning Address.",
        });
      }
      this.setIsCheckingLightning(false);
    }
  };

  @modelAction
  clear = () => {
    this.addressInput = "";
    this.addressInfo = null;
    this.lightningInfo = null;
    this.hasChecked = false;
    this.isCheckingLightning = false;
    this.resetLookup();
    // Clear URL params
    const base = window.location.origin + window.location.pathname;
    window.history.replaceState(null, "", base);
  };

  @computed
  get identifiedType(): string | null {
    return this.addressInfo?.data.type ?? null;
  }

  @computed
  get isBitcoinOrLightning(): boolean {
    return this.addressInfo?.data.isBitcoin ?? false;
  }

  @computed
  get typeLabel(): string {
    return this.addressInfo?.data.label ?? "";
  }

  @computed
  get shareableUrl(): string {
    const base = window.location.origin + window.location.pathname;
    if (this.addressInput.trim()) {
      return `${base}?address=${encodeURIComponent(this.addressInput.trim())}`;
    }
    return base;
  }

  syncUrl = () => {
    const url = this.shareableUrl;
    window.history.replaceState(null, "", url);
  };
}

const runCreateStore = () => {
  const store = new CheckBitcoinAddressStore({});
  registerRootStore(store);
  return store;
};

const createdStore = runCreateStore();

export const CheckBitcoinAddressStoreContext =
  createContext<CheckBitcoinAddressStore>(createdStore);
